import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Clock, Info, RefreshCw, Rocket } from "lucide-react";
import { api, fmtDateTime, apiError } from "@/lib/api";
import { Skeleton } from "@/components/common";
import { toast } from "sonner";

const STATUS = {
  pass: { icon: CheckCircle2, cls: "text-emerald-600", chip: "bg-emerald-100 text-emerald-700", label: "PASS" },
  warn: { icon: AlertTriangle, cls: "text-amber-500", chip: "bg-amber-100 text-amber-700", label: "WARN" },
  fail: { icon: XCircle, cls: "text-red-500", chip: "bg-red-100 text-red-700", label: "FAIL" },
  pending: { icon: Clock, cls: "text-slate-400", chip: "bg-slate-100 text-slate-600", label: "PENDING" },
  info: { icon: Info, cls: "text-sky-500", chip: "bg-sky-100 text-sky-700", label: "INFO" },
};

function Row({ it }) {
  const s = STATUS[it.status] || STATUS.info;
  const Icon = s.icon;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-slate-100 bg-white px-4 py-3" data-testid={`readiness-item-${it.key}`}>
      <Icon size={18} className={`mt-0.5 shrink-0 ${s.cls}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-[#0B132B]">{it.label}</span>
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${s.chip}`}>{s.label}</span>
        </div>
        <p className="mt-0.5 text-xs text-slate-500">{it.detail}</p>
      </div>
    </div>
  );
}

export default function ProductionReadiness() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/production-readiness").then((r) => setD(r.data)).catch((e) => { setD(false); toast.error(apiError(e)); });
  useEffect(() => { load(); }, []);

  if (d === null) return <Skeleton className="h-96" />;
  if (d === false) return <p className="text-sm text-red-500">Failed to load readiness report.</p>;

  const { summary = {}, sections = [], go_live_ready, environment, is_production } = d;
  const tiles = [
    { k: "pass", label: "Passing", cls: "text-emerald-600" },
    { k: "warn", label: "Warnings", cls: "text-amber-500" },
    { k: "fail", label: "Failing", cls: "text-red-500" },
    { k: "pending", label: "Pending", cls: "text-slate-500" },
  ];

  return (
    <div className="space-y-5" data-testid="production-readiness-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B132B]">Production Readiness</h1>
          <p className="text-sm text-slate-500">Go-Live checklist — configuration, invariants, branding & pending integrations.</p>
        </div>
        <button onClick={load} data-testid="readiness-refresh-btn"
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#0B132B] hover:bg-slate-50">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      <div className={`flex flex-wrap items-center justify-between gap-4 rounded-xl border p-5 shadow-sm ${go_live_ready ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
        data-testid="readiness-verdict">
        <div className="flex items-center gap-3">
          <div className={`grid h-11 w-11 place-items-center rounded-full ${go_live_ready ? "bg-emerald-600" : "bg-red-500"} text-white`}>
            <Rocket size={20} />
          </div>
          <div>
            <p className="font-display text-lg font-bold text-[#0B132B]">
              {go_live_ready ? "Ready to Go Live" : "Blockers Present"}
              {go_live_ready && (summary.warn || summary.pending) ? " (with notes)" : ""}
            </p>
            <p className="text-xs text-slate-600">
              Environment: <span className="font-semibold uppercase">{environment}</span>
              {is_production ? " • production security active" : " • preview mode"} · Generated {fmtDateTime(d.generated_at)}
            </p>
          </div>
        </div>
        <div className="flex gap-5">
          {tiles.map((t) => (
            <div key={t.k} className="text-center" data-testid={`readiness-count-${t.k}`}>
              <p className={`font-display text-2xl font-bold ${t.cls}`}>{summary[t.k] || 0}</p>
              <p className="text-[11px] uppercase tracking-wide text-slate-400">{t.label}</p>
            </div>
          ))}
        </div>
      </div>

      {sections.map((sec) => (
        <div key={sec.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="font-display font-bold text-[#0B132B]">{sec.title}</h3>
          <div className="mt-3 space-y-2">
            {sec.items.map((it) => <Row key={it.key} it={it} />)}
          </div>
        </div>
      ))}

      <p className="text-xs text-slate-400">
        Visibility only — this report performs no writes and changes no business logic. Rotate secrets and set
        ENVIRONMENT=production at deploy time; real Bank/NBFC & CIBIL/Experian integrations activate once official
        docs and credentials are provided.
      </p>
    </div>
  );
}
